import { useQuery } from '@tanstack/react-query';

import { useHttpClient } from '~/hooks/useHttpClient';
import { SessionStatus, Student } from '~/services/types';
import { determineRetryAndMaybeLog } from '~/utils/errors';
import { createLogger } from '~/utils/logging';

import { useReviewParams } from './useReviewParams';

const log = createLogger('useWeeklyInsightsQuery');

export const QUERY_KEY = 'weeklyInsights';

export type WeeklyInsightsRequest = {
  date: Date;
  periodId: string;
};

export type WeeklyInsightsResponse = {
  session: {
    status: SessionStatus;
  };
  periodId: string;
  weekStart: Date;
  weekEnd: Date;
  visibleFrom: Date;
  visibleTo: Date;
  sessionCount: number;
  insights: { students: Student[] }[];
};

export function useWeeklyInsights({ date, periodId }: WeeklyInsightsRequest) {
  const httpClient = useHttpClient();
  const params = useReviewParams({ date, periodId });

  const paramsQueryStr = params.toQueryString();

  const { data, error, status, refetch } = useQuery<null | WeeklyInsightsResponse>({
    queryKey: [QUERY_KEY, paramsQueryStr],
    queryFn: async () => {
      log.debug('Fetching weekly insights');

      const response = await httpClient.get<WeeklyInsightsResponse>(`/review/getInsights?${paramsQueryStr}`);
      log.info('Fetched weekly insights successfully', {
        periodId: response.periodId,
        sessionCount: response.sessionCount,
        insightCount: response.insights.length,
        studentCount: response.insights.reduce((total, i) => total + i.students.length, 0),
      });

      return response;
    },
    retry: (failureCount, error) =>
      determineRetryAndMaybeLog({ operation: 'fetch weekly insights', failureCount, error, log }),
    enabled: httpClient.isAuthenticated,
  });

  return { data, error, status, refetch };
}
